import { memo, useState } from "react";
import {
  ActionIcon,
  Box,
  Flex,
  Text,
  useMantineTheme,
  Transition,
  CloseButton,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons";
import {
  GetSignedAgreementsQuery,
  UpdateScheduledChargesInput,
  useDeleteScheduledTransactionMutation,
} from "src/generated/graphql";

import { MdPlaylistAdd } from "react-icons/md";
import { RxDotsVertical } from "react-icons/rx";
import { BiBlock } from "react-icons/bi";
import { useModals } from "@mantine/modals";
import { useParams } from "react-router-dom";
import { useClickOutside } from "@mantine/hooks";
import { CRUDSTATES } from "src/utils/constants";
import SchedulePayment from "src/modules/Client/ScheduledPayments";

type IUpcomingPaymentsProps = {
  data?: NonNullable<
    GetSignedAgreementsQuery["signedAgreementMany"]
  >[0]["subscription"];
  onRefetch: () => void;
  isAllowWrite: boolean;
};

type ISchecduledPayment = {
  state: CRUDSTATES;
  subscriptionId: string;
  scheduledTransactionId?: string;
  transactionDate?: Date;
  chargeData?: UpdateScheduledChargesInput;
};

const UpComingPayments = ({
  data,
  onRefetch,
  isAllowWrite,
}: IUpcomingPaymentsProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const { clientId } = useParams<{ clientId: string }>();
  const [activeOption, setActiveOption] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  let color = theme.colors.secondaryTextColor[2];

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const [deleteScheduleTransaction, { loading: deleteLoading }] =
    useDeleteScheduledTransactionMutation({
      onCompleted: () => {
        onRefetch();
      },
    });

  const handleDeleteScheduleTransaction = (transactionId: string) => {
    transactionId &&
      deleteScheduleTransaction({
        variables: { id: transactionId },
      });
  };

  const handleSchedulePaymentModal = ({
    state,
    subscriptionId,
    scheduledTransactionId,
    transactionDate,
  }: ISchecduledPayment) => {
    modals.openModal({
      closeOnClickOutside: true,
      withCloseButton: false,
      children: (
        <SchedulePayment
          state={state}
          clientId={clientId!}
          subscriptionId={subscriptionId}
          scheduledTransactionId={scheduledTransactionId}
          transactionDate={transactionDate}
          onRefetch={onRefetch}
          closeModal={() => modals.closeAll()}
        />
      ),
    });
  };

  return (
    <Box m={10}>
      <Flex w="100%" justify={"space-between"} align={"center"} mt={10}>
        <Text size={12} fw={500}>
          Date
        </Text>
        <Flex>
          <Text size={14} fw={600}>
            Amount
          </Text>
          {isAllowWrite && (
            <Flex ml={10} mr={5}>
              <MdPlaylistAdd
                size={25}
                onClick={() => {
                  handleSchedulePaymentModal({
                    state: CRUDSTATES.Create,
                    subscriptionId: data?.id!,
                  });
                }}
              />
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex
        direction={"column"}
        py={5}
        my={5}
        bg={theme.colors.secondary[6]}
        style={{ borderRadius: 10 }}
      >
        {data?.scheduledTransaction?.map((transaction, index) => {
          if (index > 2) return;
          const totalCost = transaction.charges?.reduce(
            (partialSum, charge) => partialSum + charge.cost,
            0
          );
          return (
            <Flex
              key={transaction.id + index}
              w="100%"
              py={10}
              px={10}
              style={{
                alignItems: "center",
                justifyContent: "space-between",
                background: index % 2 !== 0 ? theme.colors.light[1] : "",
                borderTop:
                  index % 2 !== 0 ? `1px solid ${theme.colors.light[3]}` : "",
                borderBottom:
                  index % 2 !== 0 ? `1px solid ${theme.colors.light[3]}` : "",
              }}
            >
              <Text size={12} fw={400}>
                {new Date(transaction.processAt).toDateString()}
              </Text>
              <Flex align={"center"}>
                <Text size={12} fw={400} mx={10}>
                  {`$ ${totalCost}`}
                </Text>
                {isAllowWrite && (
                  <RxDotsVertical
                    color={theme.colors.secondaryTextColor[6]}
                    size={20}
                    onClick={() =>
                      handleOpenListMenu({
                        name: "",
                        id: transaction.id,
                      })
                    }
                  />
                )}
              </Flex>
              <Transition
                mounted={activeOption?.id === transaction.id}
                transition="slide-left"
                duration={200}
                timingFunction="ease-in-out"
              >
                {(styles) => (
                  <Flex
                    my={15}
                    justify="space-between"
                    px={10}
                    py={5}
                    bg={theme.colors[theme.colorScheme][0]}
                    style={{
                      ...styles,
                      position: "absolute",
                      width: "-webkit-fill-available",
                      marginRight: "60px",
                      borderRadius: "5px",
                    }}
                    ref={outsideClickRef}
                  >
                    <ActionIcon
                      variant="light"
                      size="md"
                      color="blue.4"
                      my={4}
                      w="45%"
                      mah={25}
                      loading={false}
                      onClick={() => {
                        handleSchedulePaymentModal({
                          state: CRUDSTATES.Update,
                          subscriptionId: data.id,
                          scheduledTransactionId: transaction.id,
                          transactionDate: transaction.processAt,
                        });
                      }}
                    >
                      <>
                        <IconPencil size={21} />
                        <Text mx={10}>Edit Transaction</Text>
                      </>
                    </ActionIcon>
                    <ActionIcon
                      variant="light"
                      size="md"
                      color="red.4"
                      my={4}
                      w="45%"
                      mah={25}
                      loading={deleteLoading}
                      onClick={() => {
                        handleDeleteScheduleTransaction(transaction.id);
                      }}
                    >
                      <>
                        <BiBlock size={21} />
                        <Text mx={10}>Delete Transaction</Text>
                      </>
                    </ActionIcon>
                    <CloseButton
                      size="md"
                      w={"10%"}
                      c={theme.colors.light[5]}
                      onClick={() => setActiveOption(undefined)}
                      my={4}
                    />
                  </Flex>
                )}
              </Transition>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

export default memo(UpComingPayments);
